import { Fragment, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { datasetSliceName, userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';

import { FILE_PERMISSIONS, MODAL_MOVE_FILE, PERMISSIONS, URL_DATASET } from '@/Utils/constants';

import useOnClickOutsideHook from '@/hooks/useOnClickOutside';
import {
  selectFile,
  setCurrentDirectory,
  setCurrentFile,
  setFilePath,
  setIsLoadingLibrary,
  unSelectAllFiles,
} from '@/redux/reducers/datasetSlice';

import { get } from '@/Utils/API';
import { noteEndpoint } from '@/Utils/apiEndpoints';
import getColumnsCount from '@/Utils/getColumnsCount';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/Components/UI/Button/Button';
import Icon from '@/Components/UI/Icon/Icon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/Components/UI/Shadcn/Breadcrumbs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/Components/UI/Shadcn/DropdownMenu';
import Spinner from '@/Components/UI/Spinner/Spinner';
import { isWorldReadable } from '@/Utils/sharing';
import clsx from 'clsx';
import { getWebSocketCategory } from '../../WebSocketLibrary/WebSocketContainer';
import {
  WEBSOCKET_CATEGORY_LIBRARY,
  useWebSocketContext,
} from '../../WebSocketLibrary/WebSocketContext';
import CurrentFolderMenu from './CurrentFolderMenu';
import { FILE_TYPE_EXTERNAL } from '@/Utils/fileConstants';

const LibraryFilePath = ({ title = '' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sendMessage } = useWebSocketContext();
  const { filePath, sortingData, currentDataset, librarySearchQuery } = useSelector(
    (state) => state[datasetSliceName]
  );
  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { email, userProperties } = user;

  const actionsMenuRef = useRef();
  const [menuIsOpen, toggleActionsMenu] = useState(false);
  const [isActualDataLoading, setIsActualDataLoading] = useState(false);

  const isLastItemInFilePath = (i) => i + 1 === filePath?.length;

  const isPublic = isWorldReadable(filePath[filePath?.length - 1]?.metadata);
  const isLiveQuery = filePath[filePath?.length - 1]?.metadata?.Type === FILE_TYPE_EXTERNAL;

  useOnClickOutsideHook(menuIsOpen, actionsMenuRef, () => toggleActionsMenu(false));

  const returnDatasetName = () => {
    if (librarySearchQuery) {
      return 'Search Result';
    } else if (title) {
      return title;
    } else if (currentDataset === 'Library') {
      return 'All Files';
    } else {
      return currentDataset;
    }
  };

  const resetLibrary = async () => {
    if (!filePath.length) {
      return;
    }

    dispatch(setIsLoadingLibrary(true));
    dispatch(setFilePath([]));
    try {
      sendMessage({
        location: '',
        category: getWebSocketCategory(currentDataset),
      });
      toggleActionsMenu(() => false);
      dispatch(unSelectAllFiles());
      dispatch(setCurrentDirectory(''));
      navigate('/datasets', { replace: true });
    } catch (error) {
      console.error('Error resetting library:', error);
    } finally {
      dispatch(setIsLoadingLibrary(false));
    }
  };

  const getFileInfo = async (FileUuid) => {
    try {
      setIsActualDataLoading(true);

      const [currentFile, note, availablePermissionsJson, publicFilePermissions] =
        await Promise.all([
          get(`${URL_DATASET}/${FileUuid}`, true).then((response) => response.json()),
          get(noteEndpoint(FileUuid)),
          get(`${URL_DATASET}/${FileUuid}/${PERMISSIONS}`, true).then((response) =>
            response.json()
          ),
          get(`${URL_DATASET}/${FileUuid}/${FILE_PERMISSIONS}`, true).then((response) =>
            response.json()
          ),
        ]);

      return {
        metadata: { ...currentFile, Note: note },
        isFileShared: currentFile.Owner !== email,
        availablePermissions: availablePermissionsJson.permissions,
        IsWorldReadable: publicFilePermissions.permissions.length > 0,
        WithinQuota: currentFile.WithinQuota,
        FileColumns: getColumnsCount(currentFile),
      };
    } catch (error) {
      console.error('Error fetching file information:', error);
      throw new Error('Failed to retrieve file information.');
    } finally {
      setIsActualDataLoading(false);
    }
  };

  const handleMoveFolder = () => {
    dispatch(unSelectAllFiles());
    dispatch(selectFile(filePath[filePath?.length - 1]?.metadata));
    dispatch(showModal({ name: MODAL_MOVE_FILE, props: { isMovingSelected: true } }));
  };

  const handlePrevFolderClick = async (FileUuid) => {
    navigate(`/datasets/${FileUuid}`, { replace: true });

    sendMessage({
      location: `${FileUuid}`,
      category: WEBSOCKET_CATEGORY_LIBRARY,
    });

    const prevFileInfo = await getFileInfo(FileUuid);

    const actions = [
      dispatch(setIsLoadingLibrary(true)),
      dispatch(unSelectAllFiles()),
      dispatch(setCurrentDirectory(FileUuid)),
      dispatch(setCurrentFile({ currentFile: prevFileInfo, user: user })),
    ];

    await Promise.all(actions);
  };

  const handleCurrentFolderClick = async (FileUuid) => {
    navigate(`/datasets/${FileUuid}`, { replace: true });

    const currentFileInfo = await getFileInfo(FileUuid);
    dispatch(setCurrentFile({ currentFile: currentFileInfo, user: user }));
  };

  const renderPrevFolder = ({ FileName, FileUuid }) => {
    return (
      <>
        <BreadcrumbItem
          data-testid={`file-path-${FileName}`}
          data-cy='file-path__parent-directory'
          onClick={() => handlePrevFolderClick(FileUuid)}
        >
          {FileName}
        </BreadcrumbItem>
        {filePath.length !== 1 ? <BreadcrumbSeparator>/</BreadcrumbSeparator> : null}
      </>
    );
  };

  const renderCurrentFolder = ({ FileName, FileUuid }) => {
    return (
      <>
        <BreadcrumbItem
          data-testid={`file-path-${FileName}`}
          data-cy='file-path__current-directory'
          className='font-medium'
          onClick={() => handleCurrentFolderClick(FileUuid)}
        >
          {FileName}
        </BreadcrumbItem>
        {isActualDataLoading ? (
          <Spinner />
        ) : (
          <CurrentFolderMenu
            filePath={filePath}
            sortingData={sortingData}
            handleMoveFolder={handleMoveFolder}
            isViewerAnalyst={isViewerAnalyst}
            isLiveQuery={isLiveQuery}
            canShareLiveQuery={userProperties?.canShareLiveQuery}
          />
        )}
      </>
    );
  };

  const returnFilePath = () => {
    return (
      <>
        {filePath.map((folder, i) => (
          <Fragment key={folder.metadata.FileUuid}>
            {!isLastItemInFilePath(i)
              ? renderPrevFolder(folder.metadata)
              : renderCurrentFolder(folder.metadata)}
          </Fragment>
        ))}
      </>
    );
  };

  const returnClippedFilePath = () => {
    return (
      <>
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger className='flex items-center gap-1 cursor-pointer focus-visible:!border-none'>
              <Icon name='dots-three' />
              <span className='sr-only'>Toggle menu</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='flex flex-col gap-1 w-fit min-w-20 max-w-80'>
              {filePath.slice(0, -1).map(({ metadata }, i) => {
                return (
                  <Button
                    key={i}
                    variant='ghost'
                    className=''
                    onClick={() => handlePrevFolderClick(metadata?.FileUuid)}
                  >
                    {metadata?.FileName}
                    {isPublic ? <Icon name='plus-circle' /> : null}
                  </Button>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        {renderCurrentFolder(filePath[filePath?.length - 1]?.metadata)}
      </>
    );
  };

  return (
    <div className={clsx('flex items-center w-full h-12 p-2 mt-2')}>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem
            className={clsx(!filePath.length && 'font-medium')}
            onClick={resetLibrary}
            data-cy='top-library-level'
          >
            {returnDatasetName()}
          </BreadcrumbItem>
          {!librarySearchQuery ? (
            <>
              {filePath.length > 0 ? <BreadcrumbSeparator>/</BreadcrumbSeparator> : null}
              {filePath.length > 4 ? returnClippedFilePath() : returnFilePath()}
            </>
          ) : null}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default LibraryFilePath;
